import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, FileText, Pause, Play, ArrowUp, Radio, Eraser } from 'lucide-react';
import syncService from '../../services/syncService';
import Spinner from './Spinner';
import CopyButton from './CopyButton';
import { formatDateTime } from '../../utils/datetime';
import { apiErrorMessage } from '../../utils/apiError';
import './SyncConnectLogsModal.css';

// THE LOG LINES FROM A PROPERTY'S OWN SERVER.
//
// What the client streams to the hub once a minute (sync/log-stream), one line per row. The hub keeps
// five days.
//
// STREAMING IS OFF UNTIL ASKED FOR. "Stream 1 hour" queues STREAMLOGS; the client collects it on its
// next poll, streams for an hour and stops by itself. So the button's answer comes back on the client's
// schedule, and the status beside it says which of the two states it is in — waiting for the client,
// or streaming until a time the client itself reported.
//
// THE LEVEL CHIPS CHOOSE WHAT THE CLIENT SENDS. One level is chosen: the client streams it and everything more
// severe (ERROR = errors only; INFO = info, warnings and errors), whatever its own logLevel writes to disk.
// Choosing a level is a new stream request at that level, and the view shows the same levels. ERROR by default.
//
// THE TIME IS THE PROPERTY'S CLOCK. The client writes local wall-clock time with no offset and the
// ingest stores it as-is, so it is shown as written and labelled as such — converting it to the
// viewer's zone would shift it by an offset nobody recorded.

const LEVELS = ['ERROR', 'WARN', 'INFO', 'DEBUG'];
const DEFAULT_LEVEL = 'ERROR';
// A chosen level and everything more severe — what the client sends and the view shows. DEBUG takes TRACE too.
const levelsFrom = (level) => {
    const upTo = LEVELS.slice(0, LEVELS.indexOf(level) + 1);
    return level === 'DEBUG' ? [...upTo, 'TRACE', 'FATAL'] : [...upTo, 'FATAL'];
};
const POLL_MS = 15000;
const KEEP = 5000;

const propertyTime = (ts) => (ts ? String(ts).slice(0, 23).replace('T', ' ') : '');

// The lines as shown — property time, level, message; a continuation line is its message alone.
const linesAsText = (lines) => lines
    .map(l => (l.unparsed ? l.message : `${propertyTime(l.ts)} ${l.level || ''}\t${l.message}`))
    .join('\n');

const levelClass = (l) => {
    const v = String(l || '').toUpperCase();
    if (v === 'ERROR' || v === 'FATAL') return 'sclv-error';
    if (v === 'WARN') return 'sclv-warn';
    if (v === 'DEBUG' || v === 'TRACE') return 'sclv-debug';
    return '';
};

// Merge by id — a retried flush re-sends the same ids, and they overwrite rather than repeat.
const mergeLines = (held, incoming, where) => {
    const seen = new Set(held.map(l => l.id));
    const fresh = incoming.filter(l => !seen.has(l.id));
    const all = where === 'head' ? [...fresh, ...held] : [...held, ...fresh];
    return all.length > KEEP ? (where === 'head' ? all.slice(0, KEEP) : all.slice(-KEEP)) : all;
};

// Which state the last request is in. A request the client never answers is the case that matters —
// it means the client has stopped polling — and it is invisible without this line.
const StreamStatus = ({ stream }) => {
    if (!stream) return null;
    if (stream.active) {
        return (
            <div className="sclv-status sclv-status-on">
                <span className="sclv-dot" /> Streaming {stream.level || DEFAULT_LEVEL}
                {stream.level && stream.level !== 'ERROR' ? ' and above' : ' only'} until {formatDateTime(stream.until)}
            </div>
        );
    }
    if (!stream.startedAt) {
        return (
            <div className="sclv-status sclv-status-wait">
                Requested {stream.level || DEFAULT_LEVEL}{stream.level && stream.level !== 'ERROR' ? ' and above' : ' only'}{' '}
                {formatDateTime(stream.requestedAt)} &middot; waiting for the client to collect it
            </div>
        );
    }
    return (
        <div className="sclv-status">
            Last stream ended {formatDateTime(stream.until)}
        </div>
    );
};

const LiveLog = ({ syncId, active }) => {
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [older, setOlder] = useState(false);
    const [more, setMore] = useState(false);
    const [follow, setFollow] = useState(true);
    const [level, setLevel] = useState(DEFAULT_LEVEL);
    const levels = useMemo(() => levelsFrom(level), [level]);
    const levelPicked = useRef(false);   // the viewer chose a level, so the request's level no longer overrides it
    const [q, setQ] = useState('');
    const [query, setQuery] = useState('');
    const [error, setError] = useState(null);
    const [polledAt, setPolledAt] = useState(null);
    const [stream, setStream] = useState(null);
    const [starting, setStarting] = useState(false);

    const boxRef = useRef(null);
    const pinned = useRef(true);        // the view is at the bottom, so new lines should keep it there
    const restore = useRef(null);       // scroll height before older lines were prepended
    const linesRef = useRef(lines);
    linesRef.current = lines;
    // CLEAR only empties the screen. The newest line's position is kept so following carries on from
    // there, showing only what arrives after the clear; nothing is deleted, and Reload shows it all again.
    const clearedAt = useRef(null);
    const [cleared, setCleared] = useState(false);

    // Debounced so typing does not fire a search per keystroke.
    useEffect(() => {
        const t = setTimeout(() => setQuery(q.trim()), 400);
        return () => clearTimeout(t);
    }, [q]);

    const filters = { levels, q: query };

    const loadNewest = useCallback(async () => {
        setLoading(true); setError(null);
        clearedAt.current = null; setCleared(false);
        try {
            const res = await syncService.streamSyncConnectLogs(syncId, { levels, q: query });
            pinned.current = true;
            setLines(res.lines || []);
            setMore(!!res.more);
            setStream(res.stream || null);
            // Open on the level the client was last asked for, until the viewer picks one.
            if (!levelPicked.current && res.stream && res.stream.level && res.stream.level !== level) setLevel(res.stream.level);
            setPolledAt(new Date());
        } catch (e) {
            setError(apiErrorMessage(e, 'The live log could not be read'));
        } finally {
            setLoading(false);
        }
    }, [syncId, levels, level, query]);

    useEffect(() => { if (active) loadNewest(); }, [active, loadNewest]);

    // FOLLOW THE TAIL. Asks only for what came in after the newest line held.
    useEffect(() => {
        if (!active || !follow) return undefined;
        const tick = async () => {
            const held = linesRef.current;
            const after = held.length ? held[held.length - 1].sort : clearedAt.current;
            if (after == null) { loadNewest(); return; }
            try {
                const res = await syncService.streamSyncConnectLogs(syncId, { ...filters, after });
                if (res.lines && res.lines.length) setLines(prev => mergeLines(prev, res.lines, 'tail'));
                setStream(res.stream || null);
                setPolledAt(new Date());
                setError(null);
            } catch (e) {
                setError(apiErrorMessage(e, 'The live log could not be read'));
            }
        };
        const t = setInterval(tick, POLL_MS);
        return () => clearInterval(t);
    }, [active, follow, syncId, levels, query, loadNewest]);

    const startStream = async (at = level) => {
        setStarting(true); setError(null);
        try {
            const res = await syncService.startSyncConnectStream(syncId, at);
            setStream(res.stream || null);
            setFollow(true);
        } catch (e) {
            setError(apiErrorMessage(e, 'The stream could not be requested'));
        } finally {
            setStarting(false);
        }
    };

    const loadOlder = async () => {
        const first = lines[0];
        if (!first) return;
        setOlder(true); setError(null);
        try {
            const res = await syncService.streamSyncConnectLogs(syncId, { ...filters, before: first.sort });
            if (boxRef.current) restore.current = boxRef.current.scrollHeight;
            pinned.current = false;
            setLines(prev => mergeLines(prev, res.lines || [], 'head'));
            setMore(!!res.more);
        } catch (e) {
            setError(apiErrorMessage(e, 'Older lines could not be read'));
        } finally {
            setOlder(false);
        }
    };

    // Keep the reader where they were: at the bottom when following, on the same line after paging back.
    useLayoutEffect(() => {
        const box = boxRef.current;
        if (!box) return;
        if (restore.current != null) {
            box.scrollTop += box.scrollHeight - restore.current;
            restore.current = null;
        } else if (pinned.current) {
            box.scrollTop = box.scrollHeight;
        }
    }, [lines]);

    const onScroll = () => {
        const box = boxRef.current;
        if (box) pinned.current = box.scrollHeight - box.scrollTop - box.clientHeight < 40;
    };

    const clearScreen = () => {
        const held = linesRef.current;
        if (held.length) clearedAt.current = held[held.length - 1].sort;
        pinned.current = true;
        setLines([]);
        setMore(false);
        setCleared(true);
        setFollow(true);
    };

    // A level is a request to the client at that level, and the view follows it.
    const chooseLevel = (l) => {
        levelPicked.current = true;
        setLevel(l);
        startStream(l);
    };

    return (
        <>
            <div className="scl-bar">
                <button type="button" className="btn btn-primary btn-sm" onClick={() => startStream()} disabled={starting}
                    title={`Ask this property's SyncConnect to stream its ${level} log for the next hour`}>
                    <Radio size={14} /> {starting ? 'Asking…' : (stream && stream.active ? 'Restart 1 hour' : 'Stream 1 hour')}
                </button>
                <button type="button" className={`btn btn-sm ${follow ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFollow(f => !f)}
                    title={follow ? 'Stop following new lines' : 'Follow new lines as they arrive'}>
                    {follow ? <><Pause size={14} /> Following</> : <><Play size={14} /> Follow</>}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={loadNewest} disabled={loading}>
                    <RefreshCw size={14} /> Reload
                </button>
                <CopyButton getValue={() => linesAsText(linesRef.current)} label="Copy" disabled={!lines.length}
                    title={`Copy the ${lines.length} line(s) shown`}
                    onFallback={() => setError('The clipboard refused the copy — select the lines and copy them by hand')} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={clearScreen} disabled={!lines.length}
                    title="Clear the screen and show only lines that arrive from now on — nothing is deleted; Reload brings them back">
                    <Eraser size={14} /> Clear
                </button>
                <span className="sclv-levels" role="radiogroup" aria-label="Level the client sends">
                    {LEVELS.map(l => (
                        <button key={l} type="button" role="radio" disabled={starting}
                            className={`sclv-chip ${levelClass(l)}${levels.includes(l) ? ' on' : ''}`}
                            aria-checked={l === level} onClick={() => chooseLevel(l)}
                            title={l === 'ERROR' ? 'Ask the client to send errors only'
                                : `Ask the client to send ${l} and everything more severe`}>
                            {l}
                        </button>
                    ))}
                </span>
                <input type="search" className="sclv-search" placeholder="Search messages"
                    value={q} onChange={e => setQ(e.target.value)} />
            </div>

            <StreamStatus stream={stream} />

            <p className="scl-hint">
                Streaming is off until requested. Choosing a level asks SyncConnect to send that level and
                everything more severe (ERROR sends errors only). Once it collects the request on its next poll
                it sends its log once a minute for an hour, then stops. Lines are kept for five days; times
                are the property&rsquo;s own clock.
                {polledAt && <> Last checked {formatDateTime(polledAt)}.</>}
            </p>

            {error && <div className="os-msg os-msg-error scl-msg">{error}</div>}

            {loading && !lines.length ? <div className="loading-section"><Spinner /></div> : (
                lines.length === 0 ? (
                    <p className="scl-empty">
                        <FileText size={16} />
                        {cleared
                            ? `Cleared — waiting for new lines from SyncConnect${follow ? '' : ' (press Follow to keep checking)'}. Reload shows the earlier ones again.`
                            : level !== 'DEBUG' || query
                            ? `No ${level === 'ERROR' ? 'ERROR' : `${level}-or-above`} lines${query ? ' match' : ''} in the last five days.`
                            : 'Nothing streamed in the last five days. Press “Stream 1 hour” to ask this property’s SyncConnect for its log.'}
                    </p>
                ) : (
                    <div className="sclv-box" ref={boxRef} onScroll={onScroll}>
                        {more && (
                            <button type="button" className="sclv-older" onClick={loadOlder} disabled={older}>
                                <ArrowUp size={12} /> {older ? 'Loading…' : 'Load older lines'}
                            </button>
                        )}
                        {lines.map(l => (
                            <div key={l.id} className={`sclv-line ${levelClass(l.level)}${l.unparsed ? ' sclv-cont' : ''}`}>
                                {!l.unparsed && (
                                    <>
                                        <span className="sclv-ts">{propertyTime(l.ts)}</span>
                                        <span className="sclv-lvl">{l.level}</span>
                                    </>
                                )}
                                <span className="sclv-msg">{l.message}</span>
                            </div>
                        ))}
                    </div>
                )
            )}
        </>
    );
};


const SyncConnectLogsModal = ({ isOpen, onClose, syncId }) => {
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-md scl scl-wide" onClick={e => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-labelledby="scl-title">
                <div className="modal-header">
                    <h2 id="scl-title">SyncConnect logs</h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    <LiveLog syncId={syncId} active={isOpen} />
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default SyncConnectLogsModal;
